import React, { useState } from "react";
import { toast } from "sonner";
import { LockSimpleOpen, ShieldCheck } from "@phosphor-icons/react";
import { api, appUserAuth } from "../../lib/api";
import { tokenHeader, track } from "../../lib/clarity";

const TIMEFRAMES = [
  ["this_week", "This week"], ["this_month", "This month"], ["3_6_months", "In 3–6 months"],
  ["6_plus_months", "6+ months from now"], ["just_learning", "I'm just learning for now"],
];

const Check = ({ checked, onChange, children, testid }) => (
  <label className="flex items-start gap-3 cursor-pointer text-sm text-exodus-text/90">
    <input type="checkbox" data-testid={testid} checked={checked} onChange={(e) => onChange(e.target.checked)}
      className="mt-1 w-4 h-4 accent-[#C5A059] shrink-0" />
    <span>{children}</span>
  </label>
);

export default function ContactGate({ assessmentId, token, onDone, onSkip }) {
  const [f, setF] = useState({
    first_name: (appUserAuth.fullName() || "").split(" ")[0], email: appUserAuth.email() || "", phone: "", state: "",
    preferred_contact: "email", timeframe: "just_learning", consult_requested: false, consent_contact: false, consent_email: true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.first_name || !f.email) return toast.error("First name and email are required.");
    if (f.preferred_contact !== "email" && !f.phone) return toast.error("Please add a phone number for phone or text contact.");
    setSaving(true);
    try {
      const r = await api.post(`/clarity/${assessmentId}/contact`, { ...f, phone: f.phone || null, state: f.state || null }, tokenHeader(token));
      track("contact_submit");
      onDone(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Could not save. Please check your details.");
    } finally { setSaving(false); }
  };

  return (
    <form data-testid="clarity-contact-gate" onSubmit={submit} className="border border-exodus-gold/60 bg-exodus-surface p-6 sm:p-10">
      <p className="eyebrow flex items-center gap-2"><LockSimpleOpen size={14} /> Unlock your full report</p>
      <h3 className="font-display text-3xl mt-3 mb-2">Where should we send your report?</h3>
      <p className="text-sm text-exodus-muted mb-8 max-w-lg">Your full category analysis, tax-bucket review, and action plan — plus a downloadable PDF. We only ask for what's needed.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <input data-testid="contact-first-name" className="input-exodus" placeholder="First name *" value={f.first_name} onChange={(e) => set("first_name")(e.target.value)} />
        <input data-testid="contact-email" type="email" className="input-exodus" placeholder="Email *" value={f.email} onChange={(e) => set("email")(e.target.value)} />
        <input data-testid="contact-phone" type="tel" className="input-exodus" placeholder="Phone (optional)" value={f.phone} onChange={(e) => set("phone")(e.target.value)} />
        <input data-testid="contact-state" className="input-exodus" placeholder="State of residence" value={f.state} onChange={(e) => set("state")(e.target.value)} />
        <select data-testid="contact-method" className="input-exodus" value={f.preferred_contact} onChange={(e) => set("preferred_contact")(e.target.value)}>
          <option value="email">Prefer email</option><option value="phone">Prefer a phone call</option><option value="text">Prefer text</option>
        </select>
        <select data-testid="contact-timeframe" className="input-exodus" value={f.timeframe} onChange={(e) => set("timeframe")(e.target.value)}>
          {TIMEFRAMES.map(([v, l]) => <option key={v} value={v}>Talk: {l}</option>)}
        </select>
      </div>
      <div className="mt-6 space-y-4">
        <Check testid="contact-consent-email" checked={f.consent_email} onChange={set("consent_email")}>Email me a copy of my results.</Check>
        <Check testid="contact-consult" checked={f.consult_requested} onChange={(v) => setF((s) => ({ ...s, consult_requested: v, consent_contact: v || s.consent_contact }))}>
          I'd like a no-obligation conversation about my results.
        </Check>
        <Check testid="contact-consent" checked={f.consent_contact} onChange={set("consent_contact")}>
          I agree that Exodus Life &amp; Wealth may contact me about my results using the method I selected. Consent is not a condition of receiving my results, and I can opt out at any time.
        </Check>
      </div>
      <div className="mt-8 flex flex-wrap items-center gap-5">
        <button data-testid="contact-submit" type="submit" disabled={saving} className="btn-gold">{saving ? "Saving…" : "Show my full report"}</button>
        <button data-testid="contact-skip" type="button" onClick={() => { track("skip_contact"); onSkip(); }} className="text-xs text-exodus-muted hover:text-exodus-text underline">
          Continue without sharing contact info
        </button>
      </div>
      <p className="mt-6 text-xs text-exodus-muted flex items-center gap-2"><ShieldCheck size={14} className="text-exodus-gold" /> Encrypted in transit. Never sold. Never shared with advertising networks. See our <a href="/privacy" className="underline">Privacy Policy</a>.</p>
    </form>
  );
}
